import React, { useState } from 'react';
import { DEFAULT_WIDGETS } from '../defaultWidgets';
import { saveData, completeOnboarding } from '../api';
import {
  getBudgetMode,
  VCATS,
  nextDueDate,
  INCOME_TYPES,
  FREQ_LABELS,
  srcMonthly,
  getAllBills,
  getVarBudget,
  getCatSuggestions,
} from '../utils';

const COLORS = ['#ff6b6b', '#4d96ff', '#ffd93d', '#6bcb77', '#ff6bdf', '#7C6FF7', '#f0a500'];

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_ABBR    = ['Su','Mo','Tu','We','Th','Fr','Sa'];

/**
 * Mini calendar preview — shows all bills/debts plotted on the current month
 * so the user can immediately see their payment spread before finishing setup.
 */
function DueCalendar({ bills, cards }) {
  const items = [
    ...bills.map((b) => ({ name: b.name, amt: b.amt,  col: b.col, due: b.due })),
    ...(cards || []).map((c) => ({ name: c.name, amt: c.min, col: c.col, due: c.due, isDebt: true })),
  ].filter((x) => x.due);

  if (!items.length) return null;

  const now = new Date();
  const yr  = now.getFullYear();
  const mo  = now.getMonth();
  const dim = new Date(yr, mo + 1, 0).getDate();   // days in month
  const firstDow = new Date(yr, mo, 1).getDay();    // 0=Sun

  // Build flat cell array: null = padding, number = day
  const cells = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: dim }, (_, i) => i + 1),
  ];
  while (cells.length % 7) cells.push(null);

  // Group items by clamped due day
  const byDay = {};
  items.forEach((item) => {
    const d = Math.min(item.due, dim);
    (byDay[d] = byDay[d] || []).push(item);
  });

  const totalAmt = items.reduce((s, x) => s + (x.amt || 0), 0);

  return (
    <div style={{ marginBottom: 16, padding: '14px', background: 'var(--bg)', borderRadius: 12, border: '1px solid var(--bdr)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700 }}>📅 {MONTH_NAMES[mo]} Payment Calendar</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)' }}>${Math.round(totalAmt).toLocaleString()}/mo</div>
      </div>

      {/* Day-of-week header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 3 }}>
        {DAY_ABBR.map((d) => (
          <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--mut)', paddingBottom: 2 }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => {
          const due = day ? byDay[day] : null;
          return (
            <div
              key={i}
              style={{
                minHeight: 34,
                borderRadius: 6,
                background: due ? 'rgba(255,107,107,.07)' : 'transparent',
                border: `1px solid ${due ? 'rgba(255,107,107,.25)' : 'transparent'}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '3px 2px 4px',
              }}
            >
              {day && (
                <>
                  <span style={{ fontSize: 10, fontWeight: due ? 800 : 400, color: due ? 'var(--txt)' : 'var(--mut)', lineHeight: 1.2 }}>
                    {day}
                  </span>
                  {due && (
                    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', marginTop: 3 }}>
                      {due.map((item, j) => (
                        <div
                          key={j}
                          title={`${item.name} — $${item.amt}`}
                          style={{ width: 6, height: 6, borderRadius: '50%', background: item.col, flexShrink: 0 }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Day-by-day legend */}
      {Object.entries(byDay)
        .sort(([a], [b]) => +a - +b)
        .map(([day, dayItems]) => {
          const suffix = +day === 1 ? 'st' : +day === 2 ? 'nd' : +day === 3 ? 'rd' : 'th';
          return (
            <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--mut)', minWidth: 28, fontWeight: 700 }}>{day}{suffix}</span>
              <span style={{ fontSize: 11, color: 'var(--bdr)' }}>—</span>
              {dayItems.map((item, j) => (
                <span key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.col, display: 'inline-block', flexShrink: 0 }} />
                  {item.name}
                  <span style={{ color: 'var(--mut)', fontWeight: 400 }}>${item.amt}</span>
                  {item.isDebt && <span style={{ fontSize: 9, color: 'var(--mut)' }}>(min)</span>}
                </span>
              ))}
            </div>
          );
        })}
    </div>
  );
}

function fmt(n) {
  return '$' + Math.round(n).toLocaleString();
}

function HelpBox({ summary, children, show }) {
  const [open, setOpen] = useState(false);
  if (!show) return null;
  return (
    <div
      style={{
        background: 'rgba(255,217,61,.07)',
        border: '1px solid rgba(255,217,61,.22)',
        borderRadius: 10,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 14px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--ylw)', fontWeight: 600, lineHeight: 1.4 }}>
          💡 {summary}
        </span>
        <span style={{ fontSize: 10, color: 'var(--mut)', flexShrink: 0, marginLeft: 8 }}>
          {open ? '▲ less' : '▼ more'}
        </span>
      </button>
      {open && (
        <div
          style={{
            padding: '0 14px 12px',
            fontSize: 12,
            color: 'var(--mut)',
            lineHeight: 1.7,
            borderTop: '1px solid rgba(255,217,61,.15)',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

const DEFAULT_STATE = {
  cards: [],
  fixedBills: [],
  extra: 0,
  lump: 0,
  method: 'snowball',
  savContrib: 0,
  savGoal: 1000,
  savLog: [],
  checkins: [],
  paidBills: {},
  incomeLog: [],
  budgetCarryover: false,
  varSpend: {},
  savDeposits: {},
  budgetSlider: 50,
  catBudgets: {},
  defaultPaycheck: 0,
  extraGoals: [],
  savGoalName: 'Savings',
  customCats: [],
  subscriptions: [],
  incomeSources: [],
  dashWidgets: DEFAULT_WIDGETS,
  hiddenTabs: [],
  recapSeen: {},
  disabledCats: [],
  dismissedRecurring: [],
  bankBalance: 0,
  bankBalanceDate: null,
  incomeMode: 'monthly',
  monthlyIncome: 0,
  catchUpSeen: false,
};

export default function Onboarding({ username, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fieldErrs, setFieldErrs] = useState({});

  // Step 0 — Experience level
  const [budgetLevel, setBudgetLevel] = useState('');
  const isNew = budgetLevel === 'beginner';

  // Step 1 — Budget style
  const [slider, setSlider] = useState(50);

  // Step 2 — Income
  const [incomeSources, setIncomeSources] = useState([]);
  const [incName, setIncName] = useState('');
  const [incType, setIncType] = useState('job');
  const [incAmt, setIncAmt] = useState('');
  const [incFreq, setIncFreq] = useState('biweekly');
  const [incEntryMode, setIncEntryMode] = useState('percheck'); // 'percheck' | 'monthly'
  const [savAmt, setSavAmt] = useState('');

  // Step 3 — Fixed bills
  const [bills, setBills] = useState([]);
  const [billName, setBillName] = useState('');
  const [billAmt, setBillAmt] = useState('');
  const [billDue, setBillDue] = useState('');
  const [billCol, setBillCol] = useState(COLORS[0]);

  // Step 4 — Debts
  const [cards, setCards] = useState([]);
  const [cName, setCName] = useState('');
  const [cBal, setCBal] = useState('');
  const [cApr, setCApr] = useState('');
  const [cMin, setCMin] = useState('');
  const [cDue, setCDue] = useState('');
  const [cCol, setCCol] = useState(COLORS[0]);

  // Step 5 — Variable spending
  const [catBudgets, setCatBudgetsLocal] = useState({});
  const [householdSize, setHouseholdSize] = useState(1);

  // Step 6 — Feature preferences
  const [featSocial, setFeatSocial] = useState(true);
  const [featGamification, setFeatGamification] = useState(true);

  const mode = getBudgetMode(slider);
  const totalMonthly = incomeSources.reduce((s, src) => s + srcMonthly(src), 0);
  const previewMonthly =
    incEntryMode === 'monthly'
      ? parseFloat(incAmt) || 0
      : srcMonthly({ amt: incAmt, freq: incFreq });

  const go = (n) => {
    setErr('');
    setFieldErrs({});
    setStep(n);
  };

  const addIncomeSource = () => {
    if (!incAmt) {
      setErr('Enter an amount for this income source');
      return;
    }
    const label = INCOME_TYPES.find((t) => t.id === incType)?.label || 'Income';
    const name = incName.trim() || label;
    const storedFreq = incEntryMode === 'monthly' ? 'monthly' : incFreq;
    setIncomeSources((prev) => [
      ...prev,
      { id: 'inc_' + Date.now(), name, type: incType, amt: parseFloat(incAmt), freq: storedFreq },
    ]);
    setIncName('');
    setIncAmt('');
    setErr('');
  };

  const addBill = () => {
    const errs = {};
    if (!billName) errs.billName = true;
    if (!billAmt) errs.billAmt = true;
    if (Object.keys(errs).length) {
      setFieldErrs(errs);
      setErr('Please fill in the highlighted fields');
      return;
    }
    setBills((b) => [
      ...b,
      {
        id: 'bill_' + Date.now(),
        name: billName,
        amt: parseFloat(billAmt),
        due: Math.min(31, Math.max(1, parseInt(billDue) || 1)),
        col: billCol,
      },
    ]);
    setBillName('');
    setBillAmt('');
    setBillDue('');
    setErr('');
    setFieldErrs({});
  };

  const addCard = () => {
    const errs = {};
    if (!cName) errs.cName = true;
    if (!cBal) errs.cBal = true;
    if (!cApr) errs.cApr = true;
    if (!cMin) errs.cMin = true;
    if (Object.keys(errs).length) {
      setFieldErrs(errs);
      setErr('Please fill in the highlighted fields');
      return;
    }
    setCards((c) => [
      ...c,
      {
        id: 'card_' + Date.now(),
        name: cName,
        bal: parseFloat(cBal),
        apr: parseFloat(cApr),
        min: parseFloat(cMin),
        due: Math.min(31, Math.max(1, parseInt(cDue) || 1)),
        col: cCol,
      },
    ]);
    setCName('');
    setCBal('');
    setCApr('');
    setCMin('');
    setCDue('');
    setErr('');
    setFieldErrs({});
  };

  const setCatBudget = (id, val) => {
    const v = parseFloat(val);
    setCatBudgetsLocal((prev) => {
      const next = { ...prev };
      if (isNaN(v) || v <= 0) delete next[id];
      else next[id] = v;
      return next;
    });
  };

  const handleFinish = async () => {
    setSaving(true);
    setErr('');
    const primaryJob = incomeSources.find((s) => s.type === 'job');
    const singlePaycheck =
      incomeSources.length === 1 &&
      primaryJob &&
      primaryJob.freq !== 'monthly' &&
      primaryJob.freq !== 'annual';
    const finalData = {
      ...DEFAULT_STATE,
      cards,
      initCards: JSON.parse(JSON.stringify(cards)),
      fixedBills: bills,
      budgetSlider: slider,
      savContrib: parseFloat(savAmt) || 0,
      incomeMode: singlePaycheck ? 'paycheck' : 'monthly',
      monthlyIncome: Math.round(totalMonthly * 100) / 100,
      defaultPaycheck: singlePaycheck ? primaryJob.amt : 0,
      incomeSources,
      catBudgets,
      householdSize,
      budgetLevel,
      features: { social: featSocial, gamification: featGamification },
    };
    try {
      const saveRes = await saveData(finalData);
      if (saveRes.error) throw new Error(saveRes.error);
      const ciRes = await completeOnboarding();
      if (ciRes && ciRes.error) throw new Error(ciRes.error);
      onComplete(finalData);
    } catch (e) {
      setErr('Failed to save your setup — please try again. (' + (e.message || 'network error') + ')');
      setSaving(false);
    }
  };

  const steps = ['Budget', 'Income', 'Bills', 'Debts', 'Spending', 'Features', 'Done'];

  return (
    <div className="ob-wrap">
      <div className="ob-card">
        {/* Progress bar — hidden on experience gate */}
        {step > 0 && (
          <div className="ob-prog">
            {steps.map((s, i) => (
              <div
                key={s}
                className={
                  'ob-step' + (i + 1 === step ? ' on' : '') + (i + 1 < step ? ' done' : '')
                }
              >
                <div className="ob-dot">{i + 1 < step ? '✓' : i + 1}</div>
                <div className="ob-slbl">{s}</div>
              </div>
            ))}
          </div>
        )}

        {err && (
          <div className="lerr" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}

        {/* ── Step 0 — Experience Gate ─────────────────────────────────────── */}
        {step === 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👋</div>
            <h2 className="ob-title">Welcome, {username}!</h2>
            <p className="ob-sub" style={{ marginBottom: 24 }}>
              Before we dive in — how familiar are you with budgeting?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              {[
                {
                  id: 'beginner',
                  em: '🌱',
                  title: 'New to budgeting',
                  desc: 'Walk me through each section — I want to understand what everything means.',
                },
                {
                  id: 'experienced',
                  em: '🚀',
                  title: "I know what I'm doing",
                  desc: 'Clean setup, no extra explanations needed.',
                },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setBudgetLevel(opt.id)}
                  style={{
                    padding: '16px 18px',
                    textAlign: 'left',
                    borderRadius: 12,
                    cursor: 'pointer',
                    background: budgetLevel === opt.id ? 'rgba(255,217,61,.12)' : 'var(--surf2)',
                    border:
                      budgetLevel === opt.id ? '2px solid var(--ylw)' : '1.5px solid var(--bdr)',
                    color: 'var(--txt)',
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 5 }}>{opt.em}</div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3 }}>{opt.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--mut)' }}>{opt.desc}</div>
                </button>
              ))}
            </div>
            {budgetLevel && (
              <button className="btn" onClick={() => go(1)}>
                Let's go →
              </button>
            )}
          </div>
        )}

        {/* ── Step 1 — Budget Style ────────────────────────────────────────── */}
        {step === 1 && (
          <div>
            <h2 className="ob-title">⚖️ Budget Style</h2>
            <p className="ob-sub">How strict do you want your spending budget to be?</p>
            <HelpBox
              show={isNew}
              summary="This controls how much of your leftover money is available for flexible day-to-day spending."
            >
              After your bills and savings are covered, you have "free money" left over. This slider
              sets what percentage of that goes toward things like eating out, shopping, and
              entertainment — expenses that vary month to month.
              <br />
              <br />
              The rest gets set aside automatically for debt payoff or extra savings.{' '}
              <strong>Start at 50% if you're unsure</strong> — you can change this anytime in
              Settings.
            </HelpBox>
            <div className="bm-preview" style={{ borderColor: mode.color }}>
              <div className="bm-emoji">{mode.emoji}</div>
              <div>
                <div className="bm-name" style={{ color: mode.color }}>
                  {mode.name} Mode
                </div>
                <div className="bm-desc">{mode.desc}</div>
              </div>
            </div>
            <div style={{ margin: '20px 0' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: 'var(--mut)',
                  marginBottom: 6,
                }}
              >
                <span>📊 Tracker</span>
                <span>⚖️ Moderate</span>
                <span>🔒 Strict</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={slider}
                onChange={(e) => setSlider(+e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <button className="btn" onClick={() => go(2)}>
              Continue →
            </button>
          </div>
        )}

        {/* ── Step 2 — Income ──────────────────────────────────────────────── */}
        {step === 2 && (
          <div>
            <h2 className="ob-title">💸 Your Income</h2>
            <p className="ob-sub">Add all the ways you bring in money each month.</p>
            <HelpBox
              show={isNew}
              summary="Include your main job and any other income — freelance, side gigs, rental, benefits, etc."
            >
              The more complete your income picture, the more accurate your budget will be. If an
              amount varies (like freelance or gig work), use your average monthly amount.
              <br />
              <br />
              <strong>Savings contribution</strong> is the amount you plan to move to savings each
              month. It's protected from your spending budget — the app won't count it as money
              available to spend.
            </HelpBox>

            {/* Add income source form */}
            <div
              style={{
                background: 'var(--surf2)',
                borderRadius: 10,
                padding: '12px 14px',
                border: '1px solid var(--bdr)',
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--mut)',
                  textTransform: 'uppercase',
                  letterSpacing: '.4px',
                  marginBottom: 10,
                }}
              >
                Add Income Source
              </div>
              <div className="lf">
                <label>Type</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {INCOME_TYPES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setIncType(t.id)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: incType === t.id ? 'var(--ylw)' : 'var(--bg)',
                        color: incType === t.id ? '#000' : 'var(--txt)',
                        border:
                          incType === t.id ? '1.5px solid var(--ylw)' : '1.5px solid var(--bdr)',
                      }}
                    >
                      {t.em} {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="lf">
                <label>
                  Name <span style={{ color: 'var(--mut)', fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  className="ob-inp"
                  placeholder={`e.g. ${INCOME_TYPES.find((t) => t.id === incType)?.label}`}
                  value={incName}
                  onChange={(e) => setIncName(e.target.value)}
                />
              </div>
              {/* Entry mode toggle */}
              <div className="lf">
                <label>How do you want to enter this?</label>
                <div
                  style={{
                    display: 'flex',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1.5px solid var(--bdr)',
                    width: 'fit-content',
                  }}
                >
                  {[
                    ['percheck', 'Per paycheck'],
                    ['monthly', 'Monthly total'],
                  ].map(([id, lbl]) => (
                    <button
                      key={id}
                      onClick={() => setIncEntryMode(id)}
                      style={{
                        padding: '6px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: 'none',
                        background: incEntryMode === id ? 'var(--ylw)' : 'var(--surf2)',
                        color: incEntryMode === id ? '#000' : 'var(--mut)',
                      }}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount + frequency */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div className="lf" style={{ marginBottom: 0 }}>
                    <label>
                      {incEntryMode === 'monthly' ? 'Monthly amount' : 'Amount per paycheck'}
                    </label>
                    <div className="ob-amt-wrap">
                      <span>$</span>
                      <input
                        type="number"
                        placeholder="0"
                        value={incAmt}
                        onChange={(e) => setIncAmt(e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
                {incEntryMode === 'percheck' && (
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div className="lf" style={{ marginBottom: 0 }}>
                      <label>Paid</label>
                      <select
                        value={incFreq}
                        onChange={(e) => setIncFreq(e.target.value)}
                        className="ob-sel"
                      >
                        {Object.entries(FREQ_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Transparent breakdown */}
              {parseFloat(incAmt) > 0 && (
                <div
                  style={{
                    margin: '10px 0 2px',
                    padding: '10px 12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--bdr)',
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--mut)',
                      textTransform: 'uppercase',
                      letterSpacing: '.4px',
                      marginBottom: 8,
                    }}
                  >
                    What the app will use
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: incEntryMode === 'percheck' ? '1fr 1fr 1fr' : '1fr 1fr',
                      gap: 8,
                    }}
                  >
                    {incEntryMode === 'percheck' && (
                      <div>
                        <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 2 }}>
                          Per paycheck
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                          {fmt(parseFloat(incAmt))}
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 2 }}>
                        Per month
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--grn)' }}>
                        {fmt(previewMonthly)}
                      </div>
                      {incEntryMode === 'percheck' && incFreq !== 'monthly' && (
                        <div style={{ fontSize: 9, color: 'var(--mut)', marginTop: 1 }}>
                          {fmt(parseFloat(incAmt))} ×{' '}
                          {incFreq === 'weekly'
                            ? '52÷12'
                            : incFreq === 'biweekly'
                              ? '26÷12'
                              : incFreq === 'semimonthly'
                                ? '2'
                                : '1÷12'}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 2 }}>
                        Per year
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {fmt(previewMonthly * 12)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <button className="vad" style={{ marginTop: 10 }} onClick={addIncomeSource}>
                + Add Source
              </button>
            </div>

            {/* Income list */}
            {incomeSources.length > 0 && (
              <div className="ob-list" style={{ marginBottom: 12 }}>
                {incomeSources.map((src) => {
                  const mo = srcMonthly(src);
                  const typeInfo = INCOME_TYPES.find((t) => t.id === src.type);
                  return (
                    <div key={src.id} className="ob-item">
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{typeInfo?.em}</span>
                      <div style={{ flex: 1 }}>
                        <strong>{src.name}</strong>
                        <span style={{ color: 'var(--mut)', fontSize: 11 }}>
                          {' '}
                          — {fmt(src.amt)} {FREQ_LABELS[src.freq]?.toLowerCase()} · {fmt(mo)}/mo
                        </span>
                      </div>
                      <button
                        className="del"
                        onClick={() =>
                          setIncomeSources((prev) => prev.filter((x) => x.id !== src.id))
                        }
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
                <div
                  style={{
                    padding: '8px 4px 0',
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--grn)',
                    borderTop: '1px solid var(--bdr)',
                    marginTop: 4,
                  }}
                >
                  Total: {fmt(totalMonthly)}/mo
                </div>
              </div>
            )}

            {/* Savings */}
            <div className="lf">
              <label>
                Monthly savings contribution ($){' '}
                <span style={{ color: 'var(--mut)', fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="number"
                className="ob-inp"
                value={savAmt}
                onChange={(e) => setSavAmt(e.target.value)}
                placeholder="e.g. 150"
                min="0"
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-back" onClick={() => go(1)}>
                ← Back
              </button>
              <button className="btn" onClick={() => go(3)}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3 — Fixed Bills ──────────────────────────────────────────── */}
        {step === 3 && (
          <div>
            <h2 className="ob-title">📋 Fixed Bills</h2>
            <p className="ob-sub">
              Add recurring monthly bills — rent, car payment, insurance, phone, internet, etc.
            </p>
            <HelpBox
              show={isNew}
              summary="Fixed bills are predictable, recurring expenses — the same (or similar) amount every month."
            >
              Examples: rent or mortgage, car payment, insurance premiums, internet, cell phone,
              streaming subscriptions.
              <br />
              <br />
              <strong>Don't add groceries, gas, or dining here</strong> — those go in the Variable
              Spending step next.
              <br />
              <br />
              If a bill changes month to month (like electricity or water), just enter your typical
              amount. <strong>You can update any bill amount later in Plan → Bills</strong> — nothing is
              locked in forever.
            </HelpBox>
            <div className="ob-add-row">
              <input
                className="ob-inp"
                placeholder="Bill name"
                value={billName}
                onChange={(e) => {
                  setBillName(e.target.value);
                  setFieldErrs((f) => ({ ...f, billName: false }));
                }}
                style={fieldErrs.billName ? { border: '1.5px solid var(--red)' } : {}}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <div
                  className="ob-amt-wrap"
                  style={
                    fieldErrs.billAmt ? { border: '1.5px solid var(--red)', borderRadius: 7 } : {}
                  }
                >
                  <span>$</span>
                  <input
                    type="number"
                    placeholder="Amount"
                    value={billAmt}
                    onChange={(e) => {
                      setBillAmt(e.target.value);
                      setFieldErrs((f) => ({ ...f, billAmt: false }));
                    }}
                    min="0"
                  />
                </div>
                <input
                  className="ob-inp"
                  style={{ width: 70 }}
                  placeholder="Due date"
                  type="number"
                  min="1"
                  max="31"
                  value={billDue}
                  onChange={(e) => setBillDue(e.target.value)}
                />
              </div>
              <div className="ob-cols">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    className={'ob-col' + (billCol === c ? ' on' : '')}
                    style={{ background: c }}
                    onClick={() => setBillCol(c)}
                  />
                ))}
              </div>
              <button className="vad" onClick={addBill}>
                + Add
              </button>
            </div>
            <div className="ob-list">
              {!bills.length && <div className="em">No bills added yet — skip if none</div>}
              {bills.map((b) => (
                <div key={b.id} className="ob-item">
                  <div
                    style={{
                      background: b.col,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  ></div>
                  <div style={{ flex: 1 }}>
                    <strong>{b.name}</strong> — ${b.amt} · Due {nextDueDate(b.due)}
                  </div>
                  <button
                    className="del"
                    onClick={() => setBills((prev) => prev.filter((x) => x.id !== b.id))}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <DueCalendar bills={bills} />

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn-back" onClick={() => go(2)}>
                ← Back
              </button>
              <button className="btn" onClick={() => go(4)}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4 — Debts ───────────────────────────────────────────────── */}
        {step === 4 && (
          <div>
            <h2 className="ob-title">💳 Your Debts</h2>
            <p className="ob-sub">
              Add credit cards, loans, or any debt you're paying off. This powers the payoff
              planner.
            </p>
            <HelpBox
              show={isNew}
              summary="Track balances and interest rates to see your debt-free date and the fastest payoff strategy."
            >
              Add any debt you're actively paying down: credit cards, car loans, student loans,
              medical debt, personal loans.
              <br />
              <br />
              <strong>APR</strong> (Annual Percentage Rate) is the interest rate — find it on your
              statement or in your online account.
              <br />
              <strong>Monthly payment</strong> is the minimum required each month (you can always
              pay more).
              <br />
              <br />
              The app uses this to calculate when you'll be debt-free and recommend the best payoff
              order. <strong>Skip this step if you're debt-free!</strong>
            </HelpBox>
            <div className="ob-add-row">
              <input
                className="ob-inp"
                placeholder="Name (e.g. Chase Visa)"
                value={cName}
                onChange={(e) => {
                  setCName(e.target.value);
                  setFieldErrs((f) => ({ ...f, cName: false }));
                }}
                style={fieldErrs.cName ? { border: '1.5px solid var(--red)' } : {}}
              />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <div
                  className="ob-amt-wrap"
                  style={
                    fieldErrs.cBal ? { border: '1.5px solid var(--red)', borderRadius: 7 } : {}
                  }
                >
                  <span>$</span>
                  <input
                    type="number"
                    placeholder="Balance"
                    value={cBal}
                    onChange={(e) => {
                      setCBal(e.target.value);
                      setFieldErrs((f) => ({ ...f, cBal: false }));
                    }}
                    min="0"
                  />
                </div>
                <div
                  className="ob-amt-wrap"
                  style={
                    fieldErrs.cApr ? { border: '1.5px solid var(--red)', borderRadius: 7 } : {}
                  }
                >
                  <input
                    type="number"
                    placeholder="APR %"
                    value={cApr}
                    onChange={(e) => {
                      setCApr(e.target.value);
                      setFieldErrs((f) => ({ ...f, cApr: false }));
                    }}
                    min="0"
                    step=".01"
                  />
                  <span>%</span>
                </div>
                <div
                  className="ob-amt-wrap"
                  style={
                    fieldErrs.cMin ? { border: '1.5px solid var(--red)', borderRadius: 7 } : {}
                  }
                >
                  <span>$</span>
                  <input
                    type="number"
                    placeholder="Monthly payment"
                    value={cMin}
                    onChange={(e) => {
                      setCMin(e.target.value);
                      setFieldErrs((f) => ({ ...f, cMin: false }));
                    }}
                    min="0"
                  />
                </div>
                <input
                  className="ob-inp"
                  style={{ width: 70 }}
                  placeholder="Due date"
                  type="number"
                  min="1"
                  max="31"
                  value={cDue}
                  onChange={(e) => setCDue(e.target.value)}
                />
              </div>
              <div className="ob-cols">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    className={'ob-col' + (cCol === c ? ' on' : '')}
                    style={{ background: c }}
                    onClick={() => setCCol(c)}
                  />
                ))}
              </div>
              <button className="vad" onClick={addCard}>
                + Add
              </button>
            </div>
            <div className="ob-list">
              {!cards.length && <div className="em">No debts added yet — skip if debt-free!</div>}
              {cards.map((c) => (
                <div key={c.id} className="ob-item">
                  <div
                    style={{
                      background: c.col,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  ></div>
                  <div style={{ flex: 1 }}>
                    <strong>{c.name}</strong> — ${c.bal} at {c.apr}% APR, ${c.min}/mo
                  </div>
                  <button
                    className="del"
                    onClick={() => setCards((prev) => prev.filter((x) => x.id !== c.id))}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <DueCalendar bills={bills} cards={cards} />

            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn-back" onClick={() => go(3)}>
                ← Back
              </button>
              <button className="btn" onClick={() => go(5)}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 5 — Variable Spending ───────────────────────────────────── */}
        {step === 5 && (
          <div>
            <h2 className="ob-title">🛒 Variable Spending</h2>
            <p className="ob-sub">
              Set optional monthly limits for your everyday spending categories.
            </p>
            <HelpBox
              show={isNew}
              summary="Variable expenses change every month — groceries, dining out, gas, fun money, etc. Set goals here."
            >
              Unlike fixed bills, variable expenses aren't the same every month. Setting a limit
              here isn't a hard stop — it's a goal the app tracks for you and warns you when you're
              getting close.
              <br />
              <br />
              As you spend, you'll log purchases in the <strong>Spending tab</strong>. The app shows
              how much you've used out of each limit.
              <br />
              <br />
              <strong>Leave blank to track without a cap.</strong> You can change or add limits
              anytime from Spending → Manage categories.
            </HelpBox>
            {(() => {
              const { budget } = getVarBudget(
                totalMonthly,
                getAllBills(cards, bills, []),
                parseFloat(savAmt) || 0,
                slider
              );
              const fill = () => {
                const sug = getCatSuggestions(budget, householdSize);
                setCatBudgetsLocal(Object.fromEntries(Object.entries(sug).map(([id, x]) => [id, x.amt])));
              };
              return (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, marginRight: 4 }}>
                      How many people do you buy groceries for?
                    </span>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        onClick={() => setHouseholdSize(n)}
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          minWidth: 30,
                          padding: '4px 8px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          border: `1.5px solid ${householdSize === n ? 'var(--ylw)' : 'var(--bdr)'}`,
                          background: householdSize === n ? 'rgba(255,217,61,.12)' : 'none',
                          color: householdSize === n ? 'var(--ylw)' : 'var(--mut)',
                        }}
                      >
                        {n === 5 ? '5+' : n}
                      </button>
                    ))}
                  </div>
                  {budget > 0 && (
                    <button
                      onClick={fill}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        padding: '6px 12px',
                        borderRadius: 8,
                        background: 'var(--ylw)',
                        color: '#000',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      ✨ Fill in suggested amounts
                    </button>
                  )}
                </div>
              );
            })()}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {VCATS.map((cat) => (
                <div
                  key={cat.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 12px',
                    background: 'var(--surf2)',
                    borderRadius: 9,
                    border: '1px solid var(--bdr)',
                  }}
                >
                  <span style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}>
                    {cat.em}
                  </span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: cat.col }}>
                    {cat.name}
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      background: 'var(--bg)',
                      border: '1px solid var(--bdr)',
                      borderRadius: 7,
                      padding: '0 8px',
                    }}
                  >
                    <span style={{ fontSize: 12, color: 'var(--mut)' }}>$</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="—"
                      value={catBudgets[cat.id] || ''}
                      onChange={(e) => setCatBudget(cat.id, e.target.value)}
                      style={{
                        width: 70,
                        background: 'none',
                        border: 'none',
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--txt)',
                        padding: '6px 0',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-back" onClick={() => go(4)}>
                ← Back
              </button>
              <button className="btn" onClick={() => go(6)}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 6 — Done ────────────────────────────────────────────────── */}
        {/* ── Step 6: Feature Preferences ────────────────────────────────── */}
        {step === 6 && (
          <div>
            <h2 className="ob-title">Almost done — customize your experience</h2>
            <p className="ob-sub" style={{ marginBottom: 24 }}>
              Frujo works as a focused finance tracker <em>or</em> a social app — or both. Choose what feels right. You can change this any time in Settings.
            </p>

            {[
              {
                key: 'social',
                em: '🌐',
                title: 'Social & Community',
                desc: 'A leaderboard, clans, friend battles, a shared feed, DMs, and monthly boss events. Compete and celebrate wins with others — all income-blind, % only.',
                val: featSocial,
                set: setFeatSocial,
                onLabel: 'Join the community',
                offLabel: 'Keep it private',
              },
              {
                key: 'gamification',
                em: '⚡',
                title: 'XP, Levels & Daily Quests',
                desc: 'Earn XP for good financial habits, level up through 20 tiers, complete daily quests, and unlock badges. Turns budgeting into a game.',
                val: featGamification,
                set: setFeatGamification,
                onLabel: 'Turn it into a game',
                offLabel: 'Just the numbers',
              },
            ].map(({ key, em, title, desc, val, set, onLabel, offLabel }) => (
              <div
                key={key}
                style={{
                  marginBottom: 14,
                  borderRadius: 14,
                  border: `1.5px solid ${val ? 'var(--ylw)' : 'var(--bdr)'}`,
                  background: val ? 'rgba(255,217,61,.05)' : 'var(--surf)',
                  overflow: 'hidden',
                  transition: 'border-color .2s, background .2s',
                }}
              >
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <span style={{ fontSize: 28, marginTop: 2 }}>{em}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>{title}</div>
                      <div style={{ fontSize: 12, color: 'var(--mut)', lineHeight: 1.6, marginBottom: 12 }}>{desc}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => set(true)}
                          style={{
                            flex: 1, padding: '9px 12px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            border: `1.5px solid ${val ? 'var(--ylw)' : 'var(--bdr)'}`,
                            background: val ? 'var(--ylw)' : 'var(--surf2)',
                            color: val ? '#000' : 'var(--mut)',
                          }}
                        >
                          ✓ {onLabel}
                        </button>
                        <button
                          onClick={() => set(false)}
                          style={{
                            flex: 1, padding: '9px 12px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            border: `1.5px solid ${!val ? 'var(--bdr)' : 'var(--bdr)'}`,
                            background: !val ? 'var(--surf2)' : 'none',
                            color: !val ? 'var(--txt)' : 'var(--mut)',
                          }}
                        >
                          {offLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <p style={{ fontSize: 11, color: 'var(--mut)', textAlign: 'center', marginTop: 6, marginBottom: 16 }}>
              Nothing is locked — flip either toggle in Settings any time.
            </p>

            <button className="btn" onClick={() => go(7)}>
              Continue →
            </button>
          </div>
        )}

        {/* ── Step 7: Done ──────────────────────────────────────────────── */}
        {step === 7 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <h2 className="ob-title">You're all set!</h2>
            <p className="ob-sub" style={{ marginBottom: 24 }}>
              Here's what we set up for you:
            </p>
            <div className="ob-summary">
              <div className="ob-sum-item">
                <span>Budget Mode</span>
                <strong style={{ color: getBudgetMode(slider).color }}>
                  {getBudgetMode(slider).emoji} {getBudgetMode(slider).name}
                </strong>
              </div>
              <div className="ob-sum-item">
                <span>Monthly Income</span>
                <strong>
                  {incomeSources.length
                    ? `${fmt(totalMonthly)}/mo · ${incomeSources.length} source${incomeSources.length !== 1 ? 's' : ''}`
                    : 'Not set'}
                </strong>
              </div>
              <div className="ob-sum-item">
                <span>Fixed Bills</span>
                <strong>
                  {bills.length} bill{bills.length !== 1 ? 's' : ''} added
                </strong>
              </div>
              <div className="ob-sum-item">
                <span>Debts</span>
                <strong>
                  {cards.length} account{cards.length !== 1 ? 's' : ''} added
                </strong>
              </div>
              <div className="ob-sum-item">
                <span>Savings</span>
                <strong>${savAmt || 0}/mo</strong>
              </div>
              <div className="ob-sum-item">
                <span>Spending Limits</span>
                <strong>
                  {Object.keys(catBudgets).length} categor
                  {Object.keys(catBudgets).length !== 1 ? 'ies' : 'y'} set
                </strong>
              </div>
              <div className="ob-sum-item">
                <span>🌐 Social</span>
                <strong style={{ color: featSocial ? 'var(--grn)' : 'var(--mut)' }}>
                  {featSocial ? 'Enabled' : 'Off — private mode'}
                </strong>
              </div>
              <div className="ob-sum-item">
                <span>⚡ XP & Levels</span>
                <strong style={{ color: featGamification ? 'var(--grn)' : 'var(--mut)' }}>
                  {featGamification ? 'Enabled' : 'Off — tracker only'}
                </strong>
              </div>
            </div>
            {err && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.3)', borderRadius: 8, fontSize: 12, color: 'var(--red)' }}>
                ⚠️ {err}
              </div>
            )}
            <button
              className="btn"
              style={{ marginTop: 8 }}
              onClick={handleFinish}
              disabled={saving}
            >
              {saving ? 'Setting up...' : 'Go to My Dashboard →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
